import { toast } from "@gigpro/ui/Toast";
import { ApiError } from "@sidegigllc/api";
import type { EmployeeInterviewAdmin, EmployeeInterviewResponse } from "@sidegigllc/api/client";
import { useEmployeesInterviewsList } from "@sidegigllc/api/client";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "@tanstack/react-router";
import { useAdminPro } from "api/queries/admin";
import { marketsQueryOptions } from "api/queries/markets";
import { removeW2Onboarding, setW2Onboarding } from "api/queries/pros";
import { compareDesc } from "date-fns";
import React from "react";
import { onboardingStepsQuery } from "./queries";

export const proDetailTabs = [
  { to: "/admin/pros/$id", segment: "", label: "Overview" },
  { to: "/admin/pros/$id/application-history", segment: "application-history", label: "Application History" },
  { to: "/admin/pros/$id/job-applications", segment: "job-applications", label: "Job Applications" },
  { to: "/admin/pros/$id/performance", segment: "performance", label: "Performance" },
  { to: "/admin/pros/$id/suspensions", segment: "suspensions", label: "Suspensions" },
  { to: "/admin/pros/$id/blocks", segment: "blocks", label: "Blocks" },
  { to: "/admin/pros/$id/high-demand", segment: "high-demand", label: "High Demand" },
  { to: "/admin/pros/$id/notes", segment: "notes", label: "Notes" },
  { to: "/admin/pros/$id/sick-time", segment: "sick-time", label: "Sick Time", w2Only: true },
] as const;

export function usePageTitle(proId: number) {
  const { pathname } = useLocation();
  const { data: pro } = useAdminPro({ id: proId });
  const proName = pro ? `${pro.first_name} ${pro.last_name}` : "Loading...";

  const base = `/admin/pros/${proId}`;
  const current = proDetailTabs.find((tab) => pathname === (tab.segment ? `${base}/${tab.segment}` : base));
  if (!current) return { title: proName };

  return { title: `${proName} | ${current.label}`, currentTo: current.to };
}

export function useIsProW2(proId: number) {
  const markets = useQuery(marketsQueryOptions());
  const { data: pro } = useAdminPro({ id: proId }, { refetchOnWindowFocus: false });
  const proMarket = markets.data?.find((market) => market.id === pro?.gigster_profile?.market_id);
  const employmentTypes = proMarket?.supported_employment_types ?? [];
  return employmentTypes.length === 1 && employmentTypes.includes("W2");
}

// Endpoint returns the admin serializer for admins, the pro serializer otherwise; we only call it as admin.
const selectAdminInterviews = (interviews: Array<EmployeeInterviewResponse>) =>
  interviews.filter((interview): interview is EmployeeInterviewAdmin => "pro" in interview);

export function useLatestInterview(proId: number) {
  const isW2Pro = useIsProW2(proId);
  const query = useEmployeesInterviewsList(
    { pro: proId },
    { query: { enabled: isW2Pro, select: selectAdminInterviews } },
  );
  const activeInterview = query.data?.toSorted((a, b) => compareDesc(a.created_ts, b.created_ts))[0];
  return { ...query, data: activeInterview, isLoading: isW2Pro && query.isLoading };
}

export function useHasW2Onboarding(proId: number) {
  const onboardingSteps = useQuery(onboardingStepsQuery({ proId }));
  return onboardingSteps.data?.some((step) => step.requirement_name === "W2");
}

export function useToggleW2Onboarding(proId: number) {
  const toastIdRef = React.useRef<string | number>(undefined);

  const onboardingSteps = useQuery(onboardingStepsQuery({ proId }));
  const hasW2Onboarding = useHasW2Onboarding(proId);
  const toggleW2Onboarding = async () => {
    if (hasW2Onboarding) {
      await removeW2Onboarding(proId);
    } else {
      await setW2Onboarding(proId);
    }
  };
  return useMutation({
    mutationKey: ["toggleW2Onboarding", proId],
    mutationFn: toggleW2Onboarding,
    onMutate: () => {
      toastIdRef.current = toast.loading(
        hasW2Onboarding ? "Removing W2 onboarding step..." : "Adding W2 onboarding step...",
      );
    },
    onSuccess: () => {
      void onboardingSteps.refetch();
      toast.success("Onboarding steps updated", { id: toastIdRef.current });
    },
    onError: (error) => {
      const apiError = ApiError.fromThrown(error);
      toast.error(apiError.getMainErrorMessage(), { id: toastIdRef.current });
    },
  });
}
